import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { debounceTime, firstValueFrom, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { Tab, TabList, Tabs } from 'primeng/tabs';
import { Skeleton } from 'primeng/skeleton';
import {
  ClientForAdminTableResponse,
  ClientsClient,
  GetClientsForAdminQuery,
  IPaginationResponseOfClientForAdminTableResponse,
} from '../../../infrastructure/shared/model/fss.api';
import { Building05 } from '../../../infrastructure/shared/icons/building-05/building-05';
import { UserCircle } from '../../../infrastructure/shared/icons/user-circle/user-circle';
import { TextInput } from '../../../infrastructure/shared/ui/text-input/text-input';
import { FormControlPipe } from '../../../infrastructure/shared/pipes/form-control.pipe';
import { Search } from '../../../infrastructure/shared/icons/search/search';
import { LocationAdminService } from '../../../infrastructure/shared/services/location-admin.service';
import { DataTable } from '../../../infrastructure/shared/components/data-table/data-table';

@Component({
  standalone: true,
  selector: 'app-admin-clients-table',
  imports: [
    Tab,
    TabList,
    Tabs,
    Skeleton,
    Building05,
    UserCircle,
    TextInput,
    FormControlPipe,
    Search,
    DataTable,
  ],
  templateUrl: './admin-clients-table.html',
  styleUrl: './admin-clients-table.scss',
})
export class AdminClientsTable {
  private _subs = new Subscription();
  private _client = inject(ClientsClient);
  private _cdr = inject(ChangeDetectorRef);
  private _location = inject(LocationAdminService);

  clients: ClientForAdminTableResponse[] = [];
  first = 0;
  isLoading = true;
  activeTab = 0; // 0 = Poslovni, 1 = Individualni
  searchFormCtrl = new FormControl<string | undefined>(undefined);

  paginatedData: IPaginationResponseOfClientForAdminTableResponse = {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
  } as IPaginationResponseOfClientForAdminTableResponse;

  constructor() {
    this._subs.add(
      this.searchFormCtrl.valueChanges.pipe(debounceTime(300)).subscribe((value) => {
        this.paginatedData.currentPage = 1;
        this.load();
      }),
    );
  }

  loadLazy(event: any) {
    this.paginatedData.pageSize = event.rows;
    this.paginatedData.currentPage = Math.floor(event.first / event.rows) + 1;
    this.load();
  }

  next() {
    this.paginatedData.currentPage!++;
    this.load();
  }

  prev() {
    this.paginatedData.currentPage!--;
    this.load();
  }

  onTabChange(value: any) {
    this.activeTab = Number(value ?? 0);
    this.paginatedData.currentPage = 1;
    this.load();
  }

  load() {
    this.isLoading = true;
    this.clients = [];
    this._cdr.detectChanges();

    this._loadClients().then((res) => {
      this.clients = res.result.data ?? [];
      this.paginatedData = res.result;
      this.first = (this.paginatedData.currentPage! - 1) * this.paginatedData.pageSize!;
      this.isLoading = false;
      this._cdr.markForCheck();
    });
  }

  private async _loadClients() {
    return await firstValueFrom(
      this._client.getClientsForAdmin({
        isCompany: this.activeTab === 0,
        keyword: this.searchFormCtrl.value ?? undefined,
        pageNumber: this.paginatedData.currentPage,
        pageSize: this.paginatedData.pageSize,
      } as GetClientsForAdminQuery),
    );
  }

  getBarPercent(client: ClientForAdminTableResponse): number {
    if (!client.totalHours) return 0;
    return Math.round(((client.remainingHours ?? 0) / client.totalHours) * 100);
  }

  isBarRed(client: ClientForAdminTableResponse): boolean {
    if (!client.totalHours) return false;
    return (client.remainingHours ?? 0) / client.totalHours <= 0.2;
  }

  routeToClientDetails(id: string | undefined) {
    if (!id) return;
    this._location.routeToClientDetails(id);
  }

}
